import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';
import * as Yup from 'yup'
import clsx from 'clsx'
import { resetPassword } from '../api';
import { useHistory } from 'react-router-dom';
import { KEY } from '../../../shared/constants/shared-contants';
import {loginPageSettings} from '../redux/AuthCRUD';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';

const schema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Minimum 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .min(8, 'Minimum 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/, 'Must contain at least one special character')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Password confirmation is required')
});

const initialValues = {
  newPassword: '',
  confirmPassword: '',
}

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const history = useHistory();
  const {executeRecaptcha} = useGoogleReCaptcha();

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      if (!email || !token) {
        setStatus('The reset link is invalid or has expired. Please request a new password reset email.');
        setSubmitting(false);
        return;
      }
      setStatus(undefined);
      setTimeout(async () => {
        try {
          let captchaToken: string | null = null;

          if (captchaEnabled) {
            if (!executeRecaptcha) {
              setStatus('Captcha is not ready. Please refresh and try again.');
              setSubmitting(false);
              return;
            }
            captchaToken = await executeRecaptcha('reset_password');
          }

          await resetPassword(email, values.newPassword, values.confirmPassword, token, captchaToken);
          setStatus('Password updated successfully. Redirecting to login...');
          setTimeout(() => history.push('/auth/login'), 2000);
        } catch (error: any) {
          const message = error?.response?.data?.message ?? 'Unable to reset the password. Please request a new link.';
          setStatus(message);
        } finally {
          setSubmitting(false);
        }
      }, 1000)
    },
  });

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === KEY.enter) {
      event.preventDefault();
      if (formik.isValid && !formik.isSubmitting) {
        formik.handleSubmit();
      }
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const tokenParam = params.get('token');

    setEmail(emailParam ? decodeURIComponent(emailParam) : '');
    setToken(tokenParam ? decodeURIComponent(tokenParam) : '');
  }, []);

  useEffectOnce(() => {
    loginPageSettings()
      .then(({ data: { captchaEnable } }) => {
        setCaptchaEnabled(captchaEnable);
      });
  })

  const isFormDisabled = !email || !token

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      onKeyDown={handleFormKeyDown}
      noValidate
      id='reset-password-form'
    >
      <div className='text-center mb-9'>
        <div className='text-gray-600 fw-semibold mb-6'>Project Cost Management System</div>
        <div className='text-dark fw-bold mb-4'>Reset Password</div>
        <div className='text-start mx-auto' style={{maxWidth: '420px'}}>
          <div className='text-gray-800 fw-semibold mb-3'>Password Qualifications</div>
          <ul className='fs-7 text-gray-600 mb-0 ps-4'>
            <li>Minimum 8 characters</li>
            <li>At least 1 uppercase and 1 lowercase letter</li>
            <li>At least 1 number</li>
            <li>At least 1 special character</li>
          </ul>
        </div>
      </div>

      {formik.status && (
        <div className='mb-lg-10 alert alert-warning'>
          <div className='alert-text fw-semibold'>{formik.status}</div>
        </div>
      )}

      {!formik.status && isFormDisabled && (
        <div className='mb-lg-10 alert alert-danger'>
          <div className='alert-text fw-semibold'>
            The reset link is invalid or has expired. Please request a new password reset email.
          </div>
        </div>
      )}

      <div className='fv-row mb-6'>
        <label className='form-label fs-6 fw-bold text-dark'>
          New Password<span className='text-danger ms-1'>*</span>
        </label>
        <input
          placeholder='Enter new password'
          {...formik.getFieldProps('newPassword')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': formik.touched.newPassword && formik.errors.newPassword },
            {
              'is-valid': formik.touched.newPassword && !formik.errors.newPassword,
            }
          )}
          type='password'
          name='newPassword'
          autoComplete='off'
          disabled={isFormDisabled}
        />
        {formik.touched.newPassword && formik.errors.newPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.newPassword}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bold text-dark'>
          Confirm Password<span className='text-danger ms-1'>*</span>
        </label>
        <input
          placeholder='Re-enter new password'
          {...formik.getFieldProps('confirmPassword')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword },
            {
              'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
            }
          )}
          type='password'
          name='confirmPassword'
          autoComplete='off'
          disabled={isFormDisabled}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.confirmPassword}</span>
          </div>
        )}
      </div>

      <div className='d-grid'>
        <button
          type='submit'
          id='kt_reset_password_submit'
          className='btn btn-dark py-3'
          disabled={isFormDisabled || formik.isSubmitting || !formik.isValid}
        >
          {!formik.isSubmitting && <span className='indicator-label'>Save New Password</span>}
          {formik.isSubmitting && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Saving...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>

      <div className='border-bottom border-gray-300 my-8'></div>
      <div className='text-center'>
        <a href='mailto:support@webcert.com' className='text-gray-600 text-decoration-underline fw-semibold'>
          Need Help?
        </a>
      </div>
    </form>
  )
}

export { ResetPassword} ;
